import { useState, useEffect } from 'react';
import { useMutation } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { changeMyPassword } from '@/api/webUsers';
import { Button } from '@/components/ui/Button';

interface Props {
  open: boolean;
  onClose: () => void;
}

type FieldErrors = Partial<Record<'current_password' | 'new_password' | 'new_password_confirmation', string>>;

export function ChangeMyPasswordDialog({ open, onClose }: Props) {
  const { t } = useTranslation(['auth', 'common']);
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [fieldErrors, setFieldErrors] = useState<FieldErrors>({});
  const [topError, setTopError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setCurrentPassword('');
    setNewPassword('');
    setConfirmPassword('');
    setFieldErrors({});
    setTopError(null);
  }, [open]);

  const mutation = useMutation({
    mutationFn: () =>
      changeMyPassword({
        current_password: currentPassword,
        new_password: newPassword,
        new_password_confirmation: confirmPassword,
      }),
    onSuccess: () => {
      window.alert(t('auth:changePassword.success'));
      onClose();
    },
    onError: (err: unknown) => {
      setFieldErrors({});
      setTopError(null);

      if (err && typeof err === 'object' && 'response' in err) {
        const r = (err as { response?: { data?: { message?: string; errors?: Record<string, string[]> } } }).response;
        if (r?.data?.errors) {
          const fe: FieldErrors = {};
          for (const [key, msgs] of Object.entries(r.data.errors)) {
            const k = key as keyof FieldErrors;
            fe[k] = msgs[0];
          }
          // Friendly label for current password mismatch
          if (fe.current_password) {
            fe.current_password = t('auth:changePassword.errors.currentPasswordIncorrect');
          }
          setFieldErrors(fe);
        } else if (r?.data?.message) {
          setTopError(r.data.message);
        } else {
          setTopError(t('auth:changePassword.errors.genericFailed'));
        }
      } else if (err instanceof Error) {
        setTopError(err.message);
      } else {
        setTopError(t('auth:changePassword.errors.genericFailed'));
      }
    },
  });

  if (!open) return null;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setFieldErrors({});
    setTopError(null);
    if (newPassword.length < 8) {
      setFieldErrors({ new_password: t('auth:changePassword.errors.tooShort') });
      return;
    }
    if (newPassword !== confirmPassword) {
      setFieldErrors({ new_password_confirmation: t('auth:changePassword.errors.passwordMismatch') });
      return;
    }
    mutation.mutate();
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/40"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-labelledby="change-pwd-title"
    >
      <div
        className="bg-white w-full rounded-t-2xl sm:rounded-xl shadow-lg sm:max-w-sm flex flex-col max-h-screen sm:max-h-[90vh]"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 pt-5 pb-4 border-b border-gray-100 shrink-0">
          <h2 id="change-pwd-title" className="text-lg font-semibold text-gray-900">
            {t('auth:changePassword.title')}
          </h2>
          <button
            type="button"
            onClick={onClose}
            aria-label={t('common:actions.close')}
            className="rounded-md p-1 text-gray-400 hover:text-gray-600 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-indigo-500"
          >
            <XMarkIcon className="h-5 w-5" aria-hidden="true" />
          </button>
        </div>

        {/* Body */}
        <form onSubmit={handleSubmit} noValidate className="overflow-y-auto flex-1 px-6 py-5">
          <div className="space-y-4">
            <div>
              <label
                htmlFor="cmpd-current"
                className="block text-sm font-medium text-gray-700 mb-1"
              >
                {t('auth:changePassword.currentPasswordLabel')}
              </label>
              <input
                id="cmpd-current"
                type="password"
                value={currentPassword}
                onChange={(e) => setCurrentPassword(e.target.value)}
                required
                autoComplete="current-password"
                className="block w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 focus:outline-none"
              />
              {fieldErrors.current_password && (
                <p className="mt-1 text-xs text-red-600">{fieldErrors.current_password}</p>
              )}
            </div>

            <div>
              <label
                htmlFor="cmpd-new"
                className="block text-sm font-medium text-gray-700 mb-1"
              >
                {t('auth:changePassword.newPasswordLabel')}
              </label>
              <input
                id="cmpd-new"
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                placeholder={t('auth:changePassword.newPasswordPlaceholder')}
                required
                minLength={8}
                autoComplete="new-password"
                className="block w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 focus:outline-none"
              />
              {fieldErrors.new_password && (
                <p className="mt-1 text-xs text-red-600">{fieldErrors.new_password}</p>
              )}
            </div>

            <div>
              <label
                htmlFor="cmpd-confirm"
                className="block text-sm font-medium text-gray-700 mb-1"
              >
                {t('auth:changePassword.confirmPasswordLabel')}
              </label>
              <input
                id="cmpd-confirm"
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder={t('auth:changePassword.confirmPasswordPlaceholder')}
                required
                autoComplete="new-password"
                className="block w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 focus:outline-none"
              />
              {fieldErrors.new_password_confirmation && (
                <p className="mt-1 text-xs text-red-600">{fieldErrors.new_password_confirmation}</p>
              )}
            </div>

            {topError && <p className="text-sm text-red-600">{topError}</p>}
          </div>

          {/* Footer */}
          <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-2 pt-5 mt-2">
            <Button
              type="button"
              variant="secondary"
              onClick={onClose}
              disabled={mutation.isPending}
              className="w-full sm:w-auto"
            >
              {t('common:actions.cancel')}
            </Button>
            <Button
              type="submit"
              disabled={mutation.isPending}
              className="w-full sm:w-auto"
            >
              {mutation.isPending ? t('auth:changePassword.submitting') : t('auth:changePassword.submit')}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}
